/**
 * Strategy caching types and orchestrator — platform-agnostic extraction path persistence.
 *
 * Used by both server-side (MongoStrategyStore) and client-side (FileStrategyStore)
 * scraping to cache and replay successful extraction paths before falling back to
 * normal or AI-based extraction.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ISelectorStep {
  readonly type: 'css' | 'regex' | 'xpath' | 'evaluate' | 'ai';
  readonly value: string;
  readonly description?: string;
}

export interface IExtractionStrategy {
  readonly extractionId: string;
  readonly platform: string;
  readonly selectors: readonly ISelectorStep[];
  readonly htmlFingerprint?: string;
  readonly aiSchema?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly successCount: number;
  readonly failCount: number;
}

export interface IStrategyStore {
  get(extractionId: string): Promise<IExtractionStrategy | null>;
  save(strategy: IExtractionStrategy): Promise<void>;
  invalidate(extractionId: string): Promise<void>;
}

export interface IStrategyAttempt<T> {
  readonly extractionId: string;
  readonly platform: string;
  readonly store?: IStrategyStore;
  readonly tryCached: (strategy: IExtractionStrategy) => Promise<T | null>;
  readonly tryNormal: () => Promise<{ data: T; selectors: readonly ISelectorStep[] } | null>;
  readonly tryAi?: (
    schema: string
  ) => Promise<{ data: T; selectors: readonly ISelectorStep[] } | null>;
  readonly aiSchema?: string;
  readonly htmlFingerprint?: string;
}

// ---------------------------------------------------------------------------
// HTML Fingerprinting
// ---------------------------------------------------------------------------

export function computeFingerprint(html: string): string {
  const structural = html
    .replace(/>([^<]+)</g, '><')
    .replace(/\s+/g, ' ')
    .trim();
  // Portable SHA-256 (no node:crypto) so contracts can load in RN / browser.
  return sha256Hex(structural).slice(0, 16);
}

/** Minimal sync SHA-256 → hex. Isomorphic; used only for HTML fingerprints. */
function sha256Hex(message: string): string {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const bytes = new TextEncoder().encode(message);
  const bitLen = bytes.length * 8;
  const withPad = new Uint8Array((bytes.length + 9 + 63) & ~63);
  withPad.set(bytes);
  withPad[bytes.length] = 0x80;
  const view = new DataView(withPad.buffer);
  view.setUint32(withPad.length - 4, bitLen >>> 0, false);
  view.setUint32(withPad.length - 8, Math.floor(bitLen / 0x100000000), false);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

  for (let i = 0; i < withPad.length; i += 64) {
    for (let j = 0; j < 16; j++) w[j] = view.getUint32(i + j * 4, false);
    for (let j = 16; j < 64; j++) {
      const s0 = rotr(w[j - 15]!, 7) ^ rotr(w[j - 15]!, 18) ^ (w[j - 15]! >>> 3);
      const s1 = rotr(w[j - 2]!, 17) ^ rotr(w[j - 2]!, 19) ^ (w[j - 2]! >>> 10);
      w[j] = (w[j - 16]! + s0 + w[j - 7]! + s1) >>> 0;
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let j = 0; j < 64; j++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[j]! + w[j]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7].map((x) => x.toString(16).padStart(8, '0')).join('');
}

// ---------------------------------------------------------------------------
// useStrategy orchestrator — three-tier extraction: cached → normal → AI
// ---------------------------------------------------------------------------

export async function useStrategy<T>(attempt: IStrategyAttempt<T>): Promise<T> {
  const { extractionId, platform, store, tryCached, tryNormal, tryAi, aiSchema, htmlFingerprint } =
    attempt;

  if (store) {
    const cached = await store.get(extractionId);
    if (cached) {
      const result = await tryCached(cached);
      if (result !== null) {
        return result;
      }
      await store.invalidate(extractionId);
    }
  }

  const normalResult = await tryNormal();
  if (normalResult) {
    if (store) {
      const now = new Date().toISOString();
      await store.save({
        extractionId,
        platform,
        selectors: normalResult.selectors,
        htmlFingerprint,
        version: 1,
        createdAt: now,
        updatedAt: now,
        successCount: 1,
        failCount: 0,
      });
    }
    return normalResult.data;
  }

  if (tryAi && aiSchema) {
    const aiResult = await tryAi(aiSchema);
    if (aiResult) {
      if (store) {
        const now = new Date().toISOString();
        await store.save({
          extractionId,
          platform,
          selectors: aiResult.selectors,
          htmlFingerprint,
          aiSchema,
          version: 1,
          createdAt: now,
          updatedAt: now,
          successCount: 1,
          failCount: 0,
        });
      }
      return aiResult.data;
    }
  }

  throw new Error(`Could not extract data for ${extractionId}`);
}
